import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Directors = () => {
  const { user } = useAuth();
  const [directors, setDirectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingDirector, setEditingDirector] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    email: '',
    password: ''
  });

  const canEdit = user?.role === 'Administrador';

  useEffect(() => {
    fetchDirectors();
  }, []);

  const fetchDirectors = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/directors`);
      setDirectors(response.data);
    } catch (error) {
      toast.error('Error al cargar los directores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        role: 'Director de formación'
      };

      if (editingDirector) {
        toast.error('La edición no está disponible por el momento');
      } else {
        await axios.post(`${API_URL}/auth/register`, dataToSend);
        toast.success('Director creado correctamente');
      }
      setShowDialog(false);
      resetForm();
      fetchDirectors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el director');
    }
  };

  const handleDelete = async (directorId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este director?')) return;
    
    try {
      await axios.delete(`${API_URL}/users/${directorId}`);
      toast.success('Director eliminado correctamente');
      fetchDirectors();
    } catch (error) {
      toast.error('Error al eliminar el director');
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingDirector(null);
    setFormData({
      nombre: '',
      apellidos: '',
      email: '',
      password: ''
    });
  };

  const filteredDirectors = directors.filter(director =>
    director.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    director.apellidos.toLowerCase().includes(searchTerm.toLowerCase()) ||
    director.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="directors-container">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope font-bold text-4xl text-slate-900" data-testid="directors-title">
              Directores de Formación
            </h1>
            <p className="font-inter text-slate-600 mt-2">
              Gestiona los directores de formación
            </p>
          </div>
          {canEdit && (
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button
                  onClick={openCreateDialog}
                  className="bg-[#004E89] hover:bg-[#003B66]"
                  data-testid="directors-create-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Director
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle data-testid="director-dialog-title">
                    Crear Nuevo Director de Formación
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="director-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nombre">Nombre *</Label>
                      <Input
                        id="nombre"
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        required
                        data-testid="director-form-nombre"
                      />
                    </div>
                    <div>
                      <Label htmlFor="apellidos">Apellidos *</Label>
                      <Input
                        id="apellidos"
                        value={formData.apellidos}
                        onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
                        required
                        data-testid="director-form-apellidos"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="director-form-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Contraseña *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      data-testid="director-form-password"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="director-form-cancel">
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-[#004E89] hover:bg-[#003B66]" data-testid="director-form-submit">
                      Crear
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Buscar directores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="directors-search-input"
          />
        </div>

        {/* Directors Table */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="directors-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Nombre y Apellidos
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Email
                  </th>
                  {canEdit && (
                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDirectors.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-6 py-8 text-center text-slate-500">
                      No hay directores registrados
                    </td>
                  </tr>
                ) : (
                  filteredDirectors.map((director) => (
                    <tr
                      key={director.id}
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`director-row-${director.id}`}
                    >
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900">{director.nombre} {director.apellidos}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">{director.email}</td>
                      {canEdit && (
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(director.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`director-delete-${director.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Directors;
